// ============================================================================
// PV-1052 · M8 Patch 5b — POST helper for the public seller submission form
// Refs: Spec V1.1 §M8 L1132-1250 · UI Behavior V1.1 §6.3 L1235-1263
//       Edge Function: supabase/functions/seller-submission-create/index.ts
// ============================================================================

import { env } from './env';
import type { SellSubmissionPayload } from './sell-submission-schema';

const ENDPOINT_PATH = '/functions/v1/seller-submission-create';

export type ApiErrorCode =
  | 'invalid_payload'
  | 'invalid_photo'
  | 'recaptcha_failed'
  | 'rate_limited'
  | 'recaptcha_unreachable'
  | 'internal_error'
  | 'method_not_allowed'
  | 'network';

export type EdgeIssue = { path: string; message: string };

export type SellSubmissionResult =
  | { ok: true; referenceId: string }
  | { ok: false; code: ApiErrorCode; issues?: EdgeIssue[]; message?: string };

export type PostSellerSubmissionInput = {
  payload: SellSubmissionPayload;
  recaptchaToken: string;
  photos: ReadonlyArray<File>;
};

/**
 * POSTs the seller submission to the Edge Function. Builds the multipart
 * FormData expected by the Edge (payload JSON + recaptcha_token + photo_0..N).
 *
 * Returns a tagged-union result. The caller is responsible for surfacing the
 * `code` to the user (toast / banner / inline) and for using `issues` to
 * setError on form fields when code === 'invalid_payload'.
 */
export async function postSellerSubmission({
  payload,
  recaptchaToken,
  photos,
}: PostSellerSubmissionInput): Promise<SellSubmissionResult> {
  if (!env.supabase.url || !env.supabase.anonKey) {
    return { ok: false, code: 'internal_error', message: 'supabase_env_missing' };
  }

  const form = new FormData();
  form.set('payload', JSON.stringify(payload));
  form.set('recaptcha_token', recaptchaToken);
  photos.forEach((file, index) => {
    form.set(`photo_${index}`, file, file.name);
  });

  let resp: Response;
  try {
    resp = await fetch(`${env.supabase.url}${ENDPOINT_PATH}`, {
      method: 'POST',
      headers: {
        // The Edge has `verify_jwt = false`, but Supabase still requires the
        // anon key to route the request — without it the gateway responds 401
        // before the function ever runs.
        apikey: env.supabase.anonKey,
        Authorization: `Bearer ${env.supabase.anonKey}`,
      },
      body: form,
    });
  } catch {
    return { ok: false, code: 'network' };
  }

  // 201 Created — happy path
  if (resp.status === 201) {
    const body = (await resp.json().catch(() => null)) as { reference_id?: unknown } | null;
    const referenceId = typeof body?.reference_id === 'string' ? body.reference_id : '';
    if (!referenceId) {
      return { ok: false, code: 'internal_error', message: 'missing_reference_id' };
    }
    return { ok: true, referenceId };
  }

  // 4xx / 5xx — parse the typed Edge error envelope
  const errorBody = (await resp.json().catch(() => null)) as {
    success?: boolean;
    code?: string;
    message?: string;
    errors?: Array<{ path?: unknown; message?: unknown }>;
  } | null;

  const code = mapStatusToCode(resp.status, errorBody?.code);
  const issues = parseIssues(errorBody?.errors);
  return {
    ok: false,
    code,
    ...(issues.length > 0 ? { issues } : {}),
    ...(typeof errorBody?.message === 'string' ? { message: errorBody.message } : {}),
  };
}

function mapStatusToCode(status: number, edgeCode: string | undefined): ApiErrorCode {
  // Trust the Edge envelope first when it provides a known code, then fall
  // back to the HTTP status. This keeps the front aligned with the function
  // even if it later adds new 4xx codes (e.g. `invalid_photo` is reported as
  // 400 with code='invalid_photo' — the status alone wouldn't distinguish it
  // from a `invalid_payload`).
  switch (edgeCode) {
    case 'invalid_payload':
    case 'invalid_photo':
    case 'recaptcha_failed':
    case 'rate_limited':
    case 'recaptcha_unreachable':
    case 'internal_error':
    case 'method_not_allowed':
      return edgeCode;
  }
  if (status === 400) return 'invalid_payload';
  if (status === 403) return 'recaptcha_failed';
  if (status === 405) return 'method_not_allowed';
  if (status === 429) return 'rate_limited';
  if (status === 502) return 'recaptcha_unreachable';
  return 'internal_error';
}

function parseIssues(raw: unknown): EdgeIssue[] {
  if (!Array.isArray(raw)) return [];
  const out: EdgeIssue[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;
    const path = (entry as { path?: unknown }).path;
    const message = (entry as { message?: unknown }).message;
    if (typeof path === 'string' && typeof message === 'string') {
      out.push({ path, message });
    }
  }
  return out;
}
